import React, { FC } from "react";
import { useDispatch } from "react-redux";

import { Stack, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { useTheme } from "@mui/material/styles";

import Header from "../../components/Header";
import Page from "../../components/Page";
import RouteSelector from "../../components/RouteSelector";
import VersionGuard from "../../components/VersionGuard";
import useCurrentClusterSupportsTrafficMetrics from "../../hooks/use-current-cluster-supports-traffic-metrics";
import { setRouteId } from "../../slices/metrics";
import { useSelector } from "../../store";
import AuthorizedRequests from "./components/AuthorizedRequests";
import Bytes from "./components/Bytes";
import PeriodChange from "./components/PeriodChange";
import RequestDuration from "./components/RequestDuration";
import RequestDurationPercent from "./components/RequestDurationPercent";
import RequestsPerSecond from "./components/RequestsPerSecond";
import TimePeriodSelector from "./components/TimePeriodSelector";

const Traffic: FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { routeId } = useSelector((state) => state.metrics);
  const direction = smallScreen ? "column" : "row";
  const supportsTrafficMetrics = useCurrentClusterSupportsTrafficMetrics();

  return (
    <Page title="Traffic">
      <Header breadcrumbs={true} title="Traffic" />
      <Container maxWidth={false}>
        <VersionGuard
          docsPath="/capabilities/metrics"
          helpText="Pomerium Zero can now show detailed traffic metrics from your Pomerium installation."
          minVersionRequired="0.27.0"
          enabled={supportsTrafficMetrics}
          sx={{ mb: 4 }}
        />
        <Stack direction={direction} justifyContent="space-between" gap={5}>
          <TimePeriodSelector />
          <RouteSelector
            sx={{ m: 2 }}
            routeId={routeId}
            onRouteIdChange={(routeId) => dispatch(setRouteId(routeId || ""))}
          />
        </Stack>
        <Stack
          direction={direction}
          justifyContent="space-between"
          gap={5}
          sx={{ mt: 5 }}
        >
          <PeriodChange title="Total requests" metricId={"upstream_requests"} />
          <PeriodChange title="Authorized requests" metricId={"authz_ok"} />
        </Stack>
        <Stack
          direction={direction}
          gap={5}
          sx={{ mt: 5 }}
          justifyContent="space-between"
        >
          <AuthorizedRequests />
          <RequestDurationPercent />
        </Stack>
        <Stack direction={direction} justifyContent="center" sx={{ mt: 5 }}>
          <RequestsPerSecond />
        </Stack>
        <Stack
          direction={direction}
          justifyContent="space-between"
          gap={5}
          sx={{ mt: 5 }}
        >
          <Bytes direction="sent" />
          <Bytes direction="received" />
        </Stack>
        <Stack
          direction={direction}
          justifyContent="space-between"
          gap={5}
          sx={{ mt: 5 }}
        >
          <RequestDuration />
        </Stack>
        <Box m={3} />
      </Container>
    </Page>
  );
};

export default Traffic;
